import { CodingBundleService } from './../../../../core/services/coding-bundle.service';
import { ThemeService } from './../../../../core/services/theme.service';
import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { EMPTY, Subscription, forkJoin, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { ProcedureCode } from './../../../../core/services/app-data.service';
import { AssistantOption } from './../../../../core/services/billing.service';
import { CodePromptingService } from './../../../../core/services/code-prompting.service';
import { DisciplineConfigService } from './../../../../core/services/discipline-config.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientBillingService } from './../../../../core/services/patient-billing.service';
import { PatientService } from './../../../../core/services/patient.service';
import { UserService } from './../../../../core/services/user.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import * as _ from 'lodash';


@Component({
  selector: 'app-patient-coding-detail',
  templateUrl: './patient-coding-detail.component.html',
  styleUrls: ['./patient-coding-detail.component.scss']
})
export class PatientCodingDetailComponent extends BaseComponent implements OnInit {

  todayDate: moment.Moment = moment();

  @ViewChild('addCodeDialog') addCodeDialog?: TemplateRef<any>;
  codingBundles: any[] = [];

  form!: FormGroup;
  editingCode: any;
  patientId!: string;
  patient?: any;
  groupId!: string;
  disciplineCode: string = 'medical';

  previousAssistants: AssistantOption[] = [];
  filteredAssistants: AssistantOption[] = [];

  selectedProcedureCodes: any[] = [];
  pendingDeletions: string[] = []; // Track codes that need to be deleted on save

  // Provider selection properties
  groupUsers: any[] = [];
  showProviderSelection: boolean = false;
  selectedProviderId: string | null = null;
  public isEditingMode: boolean = false; // Track if we're in editing mode

  private routeParamsSub!: Subscription;
  darkTheme!: NgxMaterialTimepickerTheme;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private patientService: PatientService,
    private billingService: PatientBillingService,
    private codingBundleService: CodingBundleService,
    private themeService: ThemeService,
    private userService: UserService,
    private codePromptingService: CodePromptingService,
    private disciplineConfigService: DisciplineConfigService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.codingBundleService.codingBundles$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(bundles => {
        this.codingBundles = _.sortBy(bundles, 'label');;
      });


  }

  ngOnInit() {
    this.initForm();

    this.routeParamsSub = this.route.paramMap.subscribe(params => {
      const codeDate = params.get('date');
      if (codeDate) {

        this.billingService.getProcedureCodesByDate(codeDate)
          .pipe(
            takeUntil(this.destroy$)
          ).subscribe(
            results => {
              if (results) {
                this.selectedProcedureCodes = results.data;
                this.pendingDeletions = []; // Clear pending deletions when loading fresh data
                this.form.patchValue({ date: moment.utc(codeDate, 'YYYY-MM-DD'), procedureCodes: this.selectedProcedureCodes });
                this.calculatePaidCashForGroup(this.selectedProcedureCodes);
                this.calculateNoChargeForGroup(this.selectedProcedureCodes);
                
                // Set editing mode when loading existing codes
                this.isEditingMode = this.selectedProcedureCodes.length > 0;
                this.determineProviderSelection();
              }

            }
          );

      } else {
        this.resetForm();
      }
    });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.disciplineCode = this.disciplineConfigService.getDisciplineCodeForSpeciality(selectedGroup.groupSpeciality);
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();



    this.patientService.currentPatient$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(patient => {
        this.patient = patient;
        if (patient) {
          this.patientId = patient.id;
          this.form.patchValue({ patientId: this.patientId, groupId: patientGroupId(patient, this.groupId) });
        }
      });

    this.themeService.isDarkMode$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(isDark => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDark);
      });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
  }

  groupUsersSub: Subscription | null = null;
  initForm() {
    this.form = this.fb.group({
      date: [moment(), Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patientId, Validators.required],
      procedureCodes: [[], Validators.required],
      paidCash: [false],
      noCharge: [false],
      providerId: [null], // Add providerId field
      codingBundle: [null], // Add codingBundle field
    });

    // Subscribe to group users for provider selection
    if (this.groupUsersSub) {
      this.groupUsersSub.unsubscribe();
    }
    this.groupUsersSub = this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any[]) => {
        if (users && users.length > 0) {
          this.groupUsers = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50') || [];
          this.determineProviderSelection();
        }
      });
  }

  resetForm() {
    setTimeout(() => {
      this.editingCode = null;
      this.isEditingMode = false;
      this.pendingDeletions = []; // Clear pending deletions when resetting
      this.form.reset();
      this.initForm();
      this.determineProviderSelection();
      this.cd.detectChanges();
    });
  }

  calculatePaidCashForGroup(codes: any[]) {
    const notPaidCashList = codes.filter(c => !c.paidCash);
    if (notPaidCashList.length === 0) {
      this.form.patchValue({ paidCash: true });
    } else {
      this.form.patchValue({ paidCash: false });
    }
  }

  calculateNoChargeForGroup(codes: any[]) {
    const notNoChargeList = codes.filter(c => !c.noCharge);
    if (notNoChargeList.length === 0) {
      this.form.patchValue({ noCharge: true });
    } else {
      this.form.patchValue({ noCharge: false });
    }
  }

  selectCodingBundle(event: any) {
    const bundle = event.value;

    setTimeout(() => {
      if (bundle.procedureCodes) {
        this.selectedProcedureCodes = bundle.procedureCodes.map((c: any) => { return { ...c, paidCash: bundle.paidCash, noCharge: bundle.noCharge } });
        this.form.patchValue({ paidCash: bundle.paidCash, noCharge: bundle.noCharge, procedureCodes: this.selectedProcedureCodes });
        
        // Set editing mode when bundle is selected
        this.isEditingMode = this.selectedProcedureCodes.length > 0;
        this.determineProviderSelection();
      }
      this.cd.detectChanges();
    });
  }


  toggleCodeStatus(event: MatSlideToggleChange, type: string) {
    if (event.checked) {
      switch (type) {
        case 'up-front':
          this.form.patchValue({ noCharge: false });
          break;
        case 'no-charge':
          this.form.patchValue({ paidCash: false });
          break;
      }
    }

  }

  toggleIndividualCodeStatus(code: any, type: string) {
    switch (type) {
      case 'up-front':
        if (code.paidCash) {
          code.noCharge = false;
        }
        break;
      case 'no-charge':
        if (code.noCharge) {
          code.paidCash = false;
        }
        break;
    }
  }

  onProcedureCodesChanged(codes: ProcedureCode[]) {
    this.selectedProcedureCodes = codes;
    this.form.patchValue({ procedureCodes: codes });
    
    // Set editing mode if we have codes (indicating we're adding/editing codes)
    this.isEditingMode = codes.length > 0;
    this.determineProviderSelection();
  }

  async onProcedureCodeDeleted(code: ProcedureCode) {
    // List is already updated by onProcedureCodesChanged (codesChanged emits first).
    // Track server-side rows for delete-on-save; do not remove by code value or duplicates break.

    if (code.id && typeof code.id === 'string') {
      if (!this.pendingDeletions.includes(code.id)) {
        this.pendingDeletions.push(code.id);
      }
    }

    this.calculatePaidCashForGroup(this.selectedProcedureCodes);
    this.calculateNoChargeForGroup(this.selectedProcedureCodes);
  }

  openEditDialog(code: ProcedureCode): void {
    const { date } = this.form.value;
    code.date = code.date || date;
    this.editingCode = { ...code };
    
    // Ensure the editing code has a provider ID set
    if (!this.editingCode.providerId && this.selectedProviderId) {
      this.editingCode.providerId = this.selectedProviderId;
    }
    
    // Set editing mode when opening edit dialog
    this.isEditingMode = true;
    this.determineProviderSelection();

    if (this.shouldShowAssistantFields()) {
      this.loadPreviousAssistants();
    }

    this.dialog.open(this.addCodeDialog!);
  }

  private async loadPreviousAssistants(): Promise<void> {
    this.previousAssistants = await this.codePromptingService.getPreviousAssistants(this.groupId);
    this.filterAssistants(this.editingCode?.assistantName);
    this.cd.detectChanges();
  }

  filterAssistants(value: string): void {
    const filterValue = (value || '').toLowerCase();
    this.filteredAssistants = this.previousAssistants.filter(assistant => assistant.name.toLowerCase().includes(filterValue));
  }

  onAssistantSelected(name: string): void {
    const selected = this.previousAssistants.find(assistant => assistant.name === name);
    if (selected?.practiceNumber) {
      this.editingCode.assistantPracticeNumber = selected.practiceNumber;
    }
  }

  calculateBMI() {
    const weight = this.editingCode.weight;
    const height = this.editingCode.height / 100; // Convert height from cm to meters

    if (weight && height) {
      this.editingCode.BMI = (weight / (height * height)).toFixed(2).toString();
    } else {
      this.editingCode.BMI = null;
    }
  }

  private updateChipContent(code: any) {
    const { procedureCodes } = this.form.value;
    const index = (procedureCodes as any[]).findIndex(c => c.id === code.id);
    if (index >= 0) {
      procedureCodes[index] = code;
    } else {
      // For new codes, add to the array
      procedureCodes.push(code);
    }
    this.form.patchValue({ procedureCodes });
    this.dialog.closeAll();
  }


  saveCodeEdit() {
    if (typeof this.editingCode.id !== 'string') {
      this.updateChipContent(this.editingCode);
      return;
    }

    const date: moment.Moment = moment.isMoment(this.form.value.date) ? moment.utc(this.form.value.date.format('DD/MM/YYYY'), 'DD/MM/YYYY') : moment(this.form.value.date);

    // Include the provider from the editing code
    const updateData = { 
      ...this.editingCode, 
      date: date.startOf('day').toISOString(),
      providerId: this.editingCode.providerId // Use the provider selected in the dialog
    };

    this.billingService.updateCode(this.editingCode.id, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Procedure code updated successfully');
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error updating procedure code: ', error);
          this.showToastMessage('There was an error updating the procedure code. Please try again.');
        }
      });
    this.dialog.closeAll();
  }

  saveBilling() {
    const date: moment.Moment = moment.isMoment(this.form.value.date) ? moment.utc(this.form.value.date.format('DD/MM/YYYY'), 'DD/MM/YYYY') : moment(this.form.value.date);

    // Include the selected provider and pending deletions in the data
    const combinedData = {
      ...this.form.value,
      date: date.startOf('day').toISOString(),
      providerId: this.selectedProviderId, // Link codes to the selected provider
      deletedCodes: this.pendingDeletions // Send pending deletions to be processed atomically
    };

    this.billingService.saveCodeGroup(combinedData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient procedure codes saved successfully');
          this.pendingDeletions = []; // Clear pending deletions after successful save
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error saving patient procedure codes: ', error);
          this.showToastMessage('There was an error saving the patient procedure codes. Please try again.');
        }
      });
  }

  deleteCodingGroup() {
    // Collect all codes that need to be deleted (both current and pending)
    const codesToDelete: string[] = [];
    
    // Add codes from selectedProcedureCodes that have IDs
    this.selectedProcedureCodes.forEach(code => {
      if (code.id && typeof code.id === 'string' && !codesToDelete.includes(code.id)) {
        codesToDelete.push(code.id);
      }
    });
    
    // Add pending deletions that aren't already in the list
    this.pendingDeletions.forEach(codeId => {
      if (!codesToDelete.includes(codeId)) {
        codesToDelete.push(codeId);
      }
    });

    if (codesToDelete.length === 0) {
      this.showToastMessage('No codes to delete');
      return Promise.resolve();
    }

    // Create delete requests for all codes
    const requests = codesToDelete.map(codeId => 
      this.billingService.deleteCode(codeId)
        .pipe(takeUntil(this.destroy$))
        .toPromise()
        .then((response: any) => {
          // Handle the case where the code was already deleted
          if (response?.alreadyDeleted) {
            console.log(`Code ${codeId} was already deleted - treating as success`);
            return { success: true, alreadyDeleted: true };
          }
          return response;
        })
        .catch((error: any) => {
          // If it's a "not found" error, treat it as already deleted
          if (error?.status === 404 || (error?.message && error.message.includes('not found'))) {
            console.log(`Code ${codeId} not found - treating as already deleted`);
            return { success: true, alreadyDeleted: true };
          }
          throw error;
        })
    );

    return forkJoin(requests).toPromise()
      .then(() => {
        this.showToastMessage('Patient procedure codes removed successfully');
        this.pendingDeletions = []; // Clear pending deletions
        this.returnToParent();
      })
      .catch((error) => {
        console.error('Error removing patient procedure codes: ', error);
        this.showToastMessage('There was an error removing the patient procedure codes. Please try again.');
      });
  }


  async deleteCode() {
    /* const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return; */

    this.billingService.deleteCode(this.editingCode.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient procedure code deleted successfully');
          //this.returnToParent();
        },
        error: (error) => {
          console.error('Error deleting patient procedure code: ', error);
          this.showToastMessage('There was an error deleting the patient procedure code. Please try again.');
        }
      });
  }

  returnToParent() {
    // If there are pending deletions, warn the user or process them
    // For now, we'll just navigate - deletions will be processed on next save
    // Alternatively, we could show a warning if pendingDeletions.length > 0
    this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patientId}`] } }]);
  }

  shouldShowTimeFields(): boolean {
    return this.codePromptingService.shouldShowTimeFields(this.editingCode.code);
  }

  shouldShowAssistantFields(): boolean {
    return this.codePromptingService.shouldShowAssistantFields(this.editingCode.code, this.disciplineCode);
  }

  /**
   * Determines the provider selection logic:
   * - If only one provider in the group, use that as default and don't show selection
   * - If multiple providers and we're editing codes, show provider selection regardless of user role
   * - If multiple providers and not editing, use the original logic based on user role
   */
  private determineProviderSelection(): void {
    if (this.groupUsers.length === 0) {
      this.showProviderSelection = false;
      this.selectedProviderId = null;
      return;
    }

    if (this.groupUsers.length === 1) {
      // Only one provider - use as default, don't show selection
      this.showProviderSelection = false;
      this.selectedProviderId = this.groupUsers[0].userId;
      if (this.form) {
        this.form.patchValue({ providerId: this.selectedProviderId });
      }
      return;
    }

    // Multiple providers - check if we're in editing mode
    if (this.isEditingMode) {
      // When editing codes, show provider selection regardless of user role
      this.showProviderSelection = true;
      const currentUser = this.userService.user;
      const isCurrentUserPhysician = this.userService.isUserPhysician;
      
      if (isCurrentUserPhysician) {
        // Default to current physician
        this.selectedProviderId = currentUser.id;
      } else {
        // Default to group owner for non-physicians
        const groupOwner = this.groupUsers.find(user => user.userId === this.groupService.group?.userId) || this.groupUsers[0];
        this.selectedProviderId = groupOwner?.userId || null;
      }
      
      if (this.form) {
        this.form.patchValue({ providerId: this.selectedProviderId });
      }
      return;
    }

    // Not in editing mode - use original logic based on user role
    const currentUser = this.userService.user;
    const isCurrentUserPhysician = this.userService.isUserPhysician;
    if (isCurrentUserPhysician) {
      // Current user is a physician - default to them
      this.showProviderSelection = false;
      this.selectedProviderId = currentUser.id;
      if (this.form) {
        this.form.patchValue({ providerId: this.selectedProviderId });
      }
    } else {
      // Current user is not a physician - default to group owner and show dropdown
      this.showProviderSelection = true;
      // Find group owner (assuming it's the first user or a specific field)
      const groupOwner = this.groupUsers.find(user => user.userId === this.groupService.group?.userId) || this.groupUsers[0];
      this.selectedProviderId = groupOwner?.userId || null;
      if (this.form) {
        this.form.patchValue({ providerId: this.selectedProviderId });
      }
    }
  }

  /**
   * Handles provider selection change
   */
  onProviderSelectionChange(providerId: string): void {
    this.selectedProviderId = providerId;
    this.form.patchValue({ providerId: providerId });
  }

  /**
   * Handles provider selection change in the edit dialog
   */
  onEditDialogProviderChange(providerId: string): void {
    this.editingCode.providerId = providerId;
  }

}
